import { Component, PropsWithChildren } from 'react'
import * as Sentry from '@sentry/electron/renderer'
import { LoadingSpinner, ResizableDiv } from '@renderer/chat'
import { Button, Interactive, setOverlayInteractive, HighlightLogo, Tooltip } from '@highlight/ui'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer.ts'
import { User } from '@supabase/supabase-js'
import BaseIpcRenderer from '@renderer/ipc/BaseIpc.renderer.ts'
import { motion } from 'framer-motion'
import { GlobalEvents, GlobalState, GlobalStore } from '@highlight/ipc'
import { uuidV2Key } from '@shared/config/Config.ts'
import { MessageCircleQuestion, RotateCw } from 'lucide-react'
import { getInstalledVersionWithSuffix, getStartupChannel } from '@shared/utils/builds/channels.shared'

interface Props {
  children?: React.ReactNode
}

interface State {
  error: null | any
  info: null | any
  clickedHelp?: boolean
  isRestarting?: boolean
}
class OverlayErrorBoundary extends Component<Props, State> {
  constructor(props: PropsWithChildren) {
    super(props)
    this.state = {
      error: null,
      info: null,
      clickedHelp: false,
      isRestarting: false,
    }
  }

  componentDidCatch(error: Error, info: any) {
    setOverlayInteractive(false)
    GlobalEvents.emit('sidepanel:blur:close')
    this.setState({ error, info })
    Sentry.captureException(error)
    AmplitudeRenderer.trackEvent('Overlay Error', {
      error: error.message,
    })
  }

  onGetHelp = (e: React.MouseEvent) => {
    if (e) {
      e.stopPropagation()
    }

    const appVersion = getInstalledVersionWithSuffix()
    const deviceUUID = GlobalStore.device().get<string>(uuidV2Key)
    const user = GlobalState.get<User>('supabase-user')

    console.log('getting help')
    navigator.clipboard.writeText(
      JSON.stringify(
        {
          debugInfo: {
            debugId: user?.id,
            deviceId: deviceUUID,
            version: appVersion,
            channel: getStartupChannel(),
          },
          error: this.state.error.stack ?? this.state.error.message,
        },
        null,
        2,
      ),
    )
    window.open('https://highlightai.com/support', '_blank', 'noopener,noreferrer')
    this.setState({ clickedHelp: true })
    AmplitudeRenderer.trackEvent('Overlay Error Action', {
      action: 'Get Help',
      error: this.state.error?.message,
    })
  }

  onRestart = () => {
    this.setState({ isRestarting: true })
    const totalRestarts = GlobalState.get<number>('app.error.restarts', 0)
    setTimeout(() => {
      GlobalState.set<number>('app.error.restarts', (totalRestarts ?? 0) + 1)
      if (process.env.NODE_ENV === 'development' || totalRestarts === 0) {
        window.location.reload()
      } else {
        BaseIpcRenderer.restartApp()
      }
    }, 2000)
    AmplitudeRenderer.trackEvent('Overlay Error Action', {
      action: 'Restart',
      error: this.state.error?.message,
    })
  }

  render() {
    if (this.state.error) {
      return (
        <motion.div
          initial={{
            opacity: 0,
            translateX: '-50%',
            y: -20,
          }}
          animate={{
            opacity: 1,
            translateX: '-50%',
            y: 0,
          }}
          transition={{
            duration: 0.2,
          }}
          className={'group absolute left-1/2 top-4 h-12'}
        >
          <Interactive>
            <div
              className={
                'bg-background ring-red/60 hover:ring-red/80 h-full select-none overflow-hidden rounded-full ring-2'
              }
            >
              <div
                className={
                  'bg-red/20 group-hover:bg-red/30 text-foreground flex h-full w-fit items-center transition-colors'
                }
              >
                <ResizableDiv className={'!h-fit'} containerClassName={'w-fit h-full'}>
                  <div className={'flex h-full items-center gap-3 px-2.5 pl-4'}>
                    <HighlightLogo size={20} tone="light" aria-hidden>
                      <HighlightLogo.Icon />
                    </HighlightLogo>
                    <span className={'flex-shrink-0 whitespace-nowrap text-sm'}>
                      {this.state.isRestarting ? (
                        'Please wait while Highlight restarts'
                      ) : this.state.clickedHelp ? (
                        <span>
                          Please reach out in our{' '}
                          <a href={'https://discord.gg/nf6PvqRjRz'} className={'text-white underline'}>
                            #bug-reports
                          </a>{' '}
                          channel
                        </span>
                      ) : (
                        'Uh oh, something went wrong'
                      )}
                    </span>
                    <div className={'flex flex-shrink-0 items-center gap-2'}>
                      <Tooltip.Provider>
                        {!this.state.clickedHelp && (
                          <Tooltip>
                            <Tooltip.Trigger
                              render={
                                <Button
                                  variant="secondary"
                                  shape="circle"
                                  size="sm"
                                  aria-label="Copy report to clipboard and open help page"
                                  onClick={this.onGetHelp}
                                />
                              }
                            >
                              <MessageCircleQuestion />
                            </Tooltip.Trigger>
                            <Tooltip.Content>Copy report to clipboard and open help page</Tooltip.Content>
                          </Tooltip>
                        )}
                        <Tooltip>
                          <Tooltip.Trigger
                            render={
                              <Button
                                variant="secondary"
                                shape="circle"
                                size="sm"
                                aria-label="Restart Highlight"
                                onClick={this.state.isRestarting ? undefined : this.onRestart}
                              />
                            }
                          >
                            {this.state.isRestarting ? <LoadingSpinner size={'20px'} /> : <RotateCw />}
                          </Tooltip.Trigger>
                          <Tooltip.Content>Restart Highlight</Tooltip.Content>
                        </Tooltip>
                      </Tooltip.Provider>
                    </div>
                  </div>
                </ResizableDiv>
              </div>
            </div>
          </Interactive>
        </motion.div>
      )
    }
    return this.props.children
  }
}

export default OverlayErrorBoundary
